/*
 Copyright 2019-present wobe-systems GmbH

 Licensed under the Apache License, Version 2.0 (the 'License');
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

     http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an 'AS IS' BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
import { ActionTree } from 'vuex';
import * as types from '@/store/types';
import { logging } from '@/scripts/debugger';
import { OK, SERVICES } from '@/middlewares/httpClient';
import { RootState } from '../models';
import { API, APIState, Endpoint } from './models';

import { handleErrorMessage } from '../handleCatchError';
import {
  GetAPIs, AddAPI, EditAPI, DeleteApi, AddEndpoint, DeleteEndpoint, EditEndpoint,
} from './apis';

const log = (message: string) => {
  logging('VUEX/flow/actions', message);
};

const service : string = SERVICES.get('flowmanager');

export const actions: ActionTree<APIState, RootState> = {
  async [types.FETCH_APIS]({ commit, dispatch }, workspaceId: string): Promise<any> {
    commit(types.LOADING, false);

    return new Promise((resolve, reject) => {
      GetAPIs(workspaceId)
        .then(({ data }) => {
          const apis: API[] = data;

          commit(types.MUTATE_APIS, apis);
          resolve(data);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.ADD_API]({ commit, dispatch },
    {
      workspaceId,
      api,
    }: {workspaceId: string, api: API }): Promise<any> {
    return new Promise((resolve, reject) => {
      AddAPI(workspaceId, api)
        .then(({ data }) => {
          const newApi: API = data;

          commit(types.MUTATE_ADD_API, newApi);
          resolve(data);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.EDIT_API]({ commit, dispatch },
    {
      workspaceId,
      api,
    }: {workspaceId: string, api: API }): Promise<any> {
    return new Promise((resolve, reject) => {
      EditAPI(workspaceId, api)
        .then(({ data }) => {
          const newApi: API = data;

          commit(types.MUTATE_EDIT_API, newApi);
          resolve(data);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.DELETE_API](
    { commit, dispatch },
    { workspaceId, apiId }: { workspaceId: string; apiId: string },
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      DeleteApi(workspaceId, apiId)
        .then(({ status }:{status: number}) => {
          if (status === OK) {
            commit(types.MUTATE_DELETED_API, apiId);

            resolve(true);
          }
        })
        .catch((err: any) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.ADD_ENDPOINT]({ commit, dispatch },
    {
      workspaceId,
      apiId,
      endpoint,
    }: {workspaceId: string, apiId: string, endpoint: Endpoint }): Promise<any> {
    return new Promise((resolve, reject) => {
      AddEndpoint(workspaceId, apiId, endpoint)
        .then(({ data }) => {
          const newEndpoint: Endpoint = data;

          commit(types.MUTATE_ADDED_ENDPOINT, { apiId, endpoint: newEndpoint });
          resolve(data);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.EDIT_ENDPOINT]({ commit, dispatch },
    {
      workspaceId,
      apiId,
      endpoint,
    }: {workspaceId: string, apiId: string, endpoint: Endpoint }): Promise<any> {
    return new Promise((resolve, reject) => {
      EditEndpoint(workspaceId, apiId, endpoint)
        .then(({ data }) => {
          const updatedEndpoint: Endpoint = data;

          commit(types.MUTATE_EDITED_ENDPOINT, { apiId, endpoint: updatedEndpoint });
          resolve(data);
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },

  async [types.DELETE_ENDPOINT](
    { commit, dispatch },
    { workspaceId, apiId, endpointId }: { workspaceId: string; apiId: string, endpointId: string },
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      DeleteEndpoint(workspaceId, apiId, endpointId)
        .then(({ status }:{status: number}) => {
          if (status === OK) {
            commit(types.MUTATE_DELETED_ENDPOINT, { apiId, endpointId });

            resolve(true);
          }
        })
        .catch((err: any) => {
          handleErrorMessage(err, dispatch, service);
          reject(err);
        });
    });
  },
};
